import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { invitationAcceptSchema, type InvitationAcceptInput } from '@lyra/shared';
import { apiFetch, ApiClientError } from '@/lib/apiClient';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { fieldAria } from '@/lib/formA11y';

export function InvitationPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors, isSubmitting } } =
    useForm<InvitationAcceptInput>({ resolver: zodResolver(invitationAcceptSchema) });

  async function onSubmit(values: InvitationAcceptInput) {
    setFormError(null);
    try {
      await apiFetch(`/invitations/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        body: JSON.stringify({ name: values.name, password: values.password }),
      });
      navigate('/');
    } catch (e) {
      setFormError(
        e instanceof ApiClientError
          ? e.message
          : 'Invitație invalidă sau expirată. Contactați administratorul organizației.',
      );
    }
  }

  return (
    <div className="mx-auto flex min-h-full max-w-[420px] flex-col justify-center gap-8 px-6 py-16">
      <div>
        <img src="/logo-lyra.png" alt="Lyra" className="h-11 w-auto dark:hidden" />
        <div className="hidden items-center gap-2.5 dark:flex">
          <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-white ring-1 ring-black/5">
            <img src="/logo-lyra-square.png" alt="" className="size-8" />
          </span>
          <span className="text-[26px] font-bold tracking-tight text-ink">Lyra</span>
        </div>
        <p className="mt-2 text-[13.5px] text-ink-3">Activare cont</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="name">Nume</Label>
          <Input id="name" autoComplete="name" {...register('name')} {...fieldAria(!!errors.name, 'name-error')} />
          {errors.name && <p id="name-error" className="text-[12.5px] text-bad">{errors.name.message}</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">Parolă</Label>
          <Input id="password" type="password" autoComplete="new-password" {...register('password')} {...fieldAria(!!errors.password, 'password-error')} />
          {errors.password && <p id="password-error" className="text-[12.5px] text-bad">{errors.password.message}</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirmPassword">Confirmă parola</Label>
          <Input id="confirmPassword" type="password" autoComplete="new-password" {...register('confirmPassword')} {...fieldAria(!!errors.confirmPassword, 'confirmPassword-error')} />
          {errors.confirmPassword && <p id="confirmPassword-error" className="text-[12.5px] text-bad">{errors.confirmPassword.message}</p>}
        </div>
        {formError && <p className="text-[13.5px] text-bad">{formError}</p>}
        <Button type="submit" disabled={isSubmitting} className="mt-2">
          {isSubmitting ? 'Se activează…' : 'Activează contul'}
        </Button>
      </form>
    </div>
  );
}
